import { Drawer } from '@mui/material'
import type { PropsWithChildren, ReactNode } from 'react'
import React, { useState } from 'react'
import type { DrawerContextProps, DrawerViewOptions } from './DrawerContext'
import DrawerContext, { INITIAL_OPTIONS } from './DrawerContext'

type DrawerEntry = { view: ReactNode; options: DrawerViewOptions }

// Each stacked view keeps its own options, so going back restores the previous view's width,
// anchor and layering without the provider having to guess them.
const DrawerContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [views, setViews] = useState<DrawerEntry[]>([])

  const options = views[0]?.options ?? INITIAL_OPTIONS

  const setOptions: DrawerContextProps['setOptions'] = patch => {
    setViews(([current, ...rest]) =>
      current ? [{ ...current, options: { ...current.options, ...patch } }, ...rest] : rest,
    )
  }

  const openDrawer: DrawerContextProps['openDrawer'] = (view, options = {}) => {
    setViews(views => [{ view, options: { ...INITIAL_OPTIONS, ...options } }, ...views])
    setOpen(true)
  }

  const goBack = () => {
    setViews(views => views.slice(1))
  }

  // Dismiss a nested sub-flow and return to the view that launched it (the stack root).
  const goToRoot = () => {
    setViews(views => views.slice(-1))
  }

  const closeDrawer = () => {
    setOpen(false)
  }

  const handleExitTransition = () => {
    setViews([])
  }

  return (
    <DrawerContext.Provider
      value={{
        hasPreviousView: views.length > 1,
        isOpen: open,
        openDrawer,
        goBack,
        goToRoot,
        closeDrawer,
        options,
        setOptions,
      }}
    >
      {children}

      <Drawer
        open={open}
        onClose={closeDrawer}
        SlideProps={{
          onExited: handleExitTransition,
        }}
        anchor={options.anchor}
        keepMounted
        PaperProps={{
          sx: theme =>
            options.fullScreen
              ? {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: `calc(100% - ${theme.spacing(4)})`,
                  height: `calc(100% - ${theme.spacing(4)})`,
                  padding: 0,
                }
              : { width: '100%', maxWidth: options.maxWidth, padding: 0 },
        }}
        sx={({ zIndex }) => ({
          zIndex: options.showAboveDialogs ? zIndex.modal + 1 : zIndex.drawer,
        })}
      >
        {views[0]?.view}
      </Drawer>
    </DrawerContext.Provider>
  )
}

export default DrawerContextProvider
